import _extends from "@babel/runtime/helpers/extends";
import _defineProperty from "@babel/runtime/helpers/defineProperty";
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
import { toast } from '@livable/saas-react-components';
import { Button, Grid, Paper, TextField, Typography } from '@mui/material';
import { useFormik } from 'formik';
import { observer } from 'mobx-react-lite';
import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import InputMask from 'react-input-mask';
import { useStore } from 'src/stores';
import { beforeSubmitDataValidation } from 'src/core/services/beforeSubmitDataValidation';
import { personalInformationSchema } from 'src/utils/formValidation/SignUp';
import useStyles from './styles';
export const PersonalDetailsTab = observer(() => {
  const classes = useStyles();
  const store = useStore();
  const _useTranslation = useTranslation(),
    t = _useTranslation.t;
  let initial = {
    firstName: store.account.userProfile.firstName,
    lastName: store.account.userProfile.lastName,
    email: store.account.userProfile.email,
    phone: store.account.userProfile.phone ? store.account.userProfile.phone.replace('+1', '').replace('+38', '') : ''
  };
  const getProfile = async data => {
    const resp = await store.account.getProfileAsync.run(data);
    return resp;
  };
  const updateEmailByAccountId = async data => {
    const resp = await store.account.updateEmailByAccountIdAsync.run(beforeSubmitDataValidation(data));
    return resp;
  };
  const updateProfileByAccountId = async data => {
    const resp = await store.account.updateProfileByAccountIdAsync.run(beforeSubmitDataValidation(data));
    return resp;
  };
  const updatePhoneNumber = async data => {
    const resp = await store.account.updatePhoneNumberAsync.run(beforeSubmitDataValidation(data));
    return resp;
  };
  const formik = useFormik({
    initialValues: initial,
    validationSchema: personalInformationSchema,
    enableReinitialize: true,
    onSubmit: async values => {
      toast('loading', {
        body: ['Saving...']
      }); // ? i18n

      const email = values.email,
        firstName = values.firstName,
        lastName = values.lastName,
        phone = values.phone;
      const phoneCode = store.account.featureFlagsStore.isUkrainianMobileCode ? '+38' : '+1';
      const promises = [];
      if (email !== initial.email) promises.push(updateEmailByAccountId({
        accountId: store.account.userProfile.id,
        email
      }));
      if (firstName !== initial.firstName || lastName !== initial.lastName) promises.push(updateProfileByAccountId({
        accountId: store.account.userProfile.id,
        firstname: firstName,
        lastname: lastName
      }));
      if (phone !== initial.phone) promises.push(updatePhoneNumber({
        phone: "".concat(phoneCode).concat(phone.replace(/[^0-9+]/g, ''))
      }));
      if (!promises.length) return;
      Promise.all([...promises]).then(responses => {
        if (responses.some(response => !response.isSuccessful)) return;
        initial = _objectSpread({}, values);
        toast('success', {
          body: [t('general.successfully_updated')]
        });
        return getProfile();
      });
    }
  });
  useEffect(() => {
    getProfile(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const isDisabled = store.account.getProfileAsync.inProgress || store.account.updateEmailByAccountIdAsync.inProgress || store.account.updatePhoneNumberAsync.inProgress || store.account.updateProfileAsync.inProgress;
  const onBlur = e => {
    if (e.target.value) formik.handleBlur(e);
  };
  const getLabel = name => {
    switch (name) {
      case 'firstName':
        return 'first_name';
      case 'lastName':
        return 'last_name';
      case 'email':
        return 'email_address';
      case 'phone':
        return 'personal_phone_optional';
    }
  };
  const getTextField = field => /*#__PURE__*/React.createElement(Grid, {
    item: true,
    xs: field === 'email' ? 7 : 6
  }, /*#__PURE__*/React.createElement(TextField, {
    disabled: isDisabled,
    error: formik.touched[field] && Boolean(formik.errors[field]),
    fullWidth: true,
    helperText: formik.touched[field] && formik.errors[field],
    id: field,
    label: t("general.".concat(getLabel(field))),
    name: field,
    onBlur: onBlur,
    onChange: formik.handleChange,
    variant: "outlined",
    value: formik.values[field]
  }));
  return /*#__PURE__*/React.createElement("div", {
    className: classes.tabWrapper
  }, /*#__PURE__*/React.createElement(Paper, null, /*#__PURE__*/React.createElement("form", {
    onSubmit: formik.handleSubmit
  }, /*#__PURE__*/React.createElement("div", {
    className: classes.paperBody
  }, /*#__PURE__*/React.createElement(Typography, {
    variant: "h4"
  }, t('profile_settings.personal_details')), /*#__PURE__*/React.createElement("div", {
    className: classes.mdSection
  }, /*#__PURE__*/React.createElement(Grid, {
    container: true,
    rowSpacing: 24,
    columnSpacing: 10
  }, getTextField('firstName'), getTextField('lastName'), getTextField('email'), /*#__PURE__*/React.createElement(Grid, {
    item: true,
    xs: 5
  }, /*#__PURE__*/React.createElement(InputMask, {
    onBlur: e => {
      if (!formik.touched.phone && /\d/.test(e.target.value)) formik.setFieldTouched('phone', true);
    },
    onChange: formik.handleChange,
    disabled: isDisabled,
    mask: "999-999-9999",
    value: formik.values.phone
  }, inputProps => /*#__PURE__*/React.createElement(TextField, _extends({}, inputProps, {
    disabled: isDisabled,
    error: formik.touched.phone && Boolean(formik.errors.phone),
    fullWidth: true,
    helperText: formik.touched.phone && formik.errors.phone,
    id: "phone",
    label: t('general.personal_phone_optional'),
    name: "phone",
    type: "tel",
    variant: "outlined"
  }))))))), /*#__PURE__*/React.createElement("div", {
    className: classes.pageFooter
  }, /*#__PURE__*/React.createElement(Button, {
    disabled: isDisabled,
    type: "submit",
    variant: "priority",
    'custom-attribute': '732-personal-details-page'
  }, t('buttons.save_changes'))))));
});